// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

// MUI imports
import { PaletteMode, Theme, alpha } from "@mui/material";

// color design tokens export
export const tokens = (mode: PaletteMode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#ffffff",
          200: "#e0e0e0",
          300: "#a3a3a3",
          330: "#383838",
          350: "#374151",
          400: "#3d3d3d",
          450: "#151B23",
          500: "#141414",
          600: "#000000",
        },
        primary: {
          100: "#212A30",
          200: "#4C2A14",
          300: "#FD7014",
        },
        success: {
          100: "#4caf50",
        },
        warning: {
          100: "#ff9800",
        },
        error: {
          100: "#f44336",
        },
      }
    : {
        grey: {
          100: "#000000",
          200: "#141414",
          300: "#374151",
          350: "#F5F5F5",
          400: "#a3a3a3",
          500: "#393E46",
          600: "#ffffff",
        },
        primary: {
          100: "#212A30",
          200: "#fef2e6",
          300: "#FD7014",
        },
        success: {
          100: "#4caf50",
        },
        warning: {
          100: "#ff9800",
        },
        error: {
          100: "#f44336",
        },
      }),
});

// Extend the background type to include autocomplete
declare module '@mui/material/styles' {
  interface TypeBackground {
    autocomplete?: string;
    dataGrid?: string;
    layout?: string;
  }

  interface PaletteOptions {
    background?: Partial<TypeBackground>;
  }
}

// mui theme settings
export const themeSettings = (mode: PaletteMode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            primary: {
              main: colors.primary[100],
              dark: colors.primary[300],
              contrastText: colors.primary[200],
            },
            secondary: {
              dark: colors.grey[300],
              main: colors.grey[300],
              light: colors.grey[500],
            },
            success: {
              main: colors.success[100],
            },
            warning: {
              main: colors.warning[100],
            },
            error: {
              main: colors.error[100],
            },
            background: {
              default: colors.grey[500],
              form: colors.grey[330],
              banner: colors.primary[200],
              autocomplete: colors.grey[350],
              dataGrid: colors.grey[450],
              layout: colors.grey[100],
            },
            header: {
              header: "#F1F7FF",
            },
            action: {
              activatedOpacity: 1,
            },
          }
        : {
            primary: {
              main: colors.primary[100],
              dark: colors.primary[300],
              contrastText: colors.primary[200],
            },
            secondary: {
              main: colors.primary[100],
              light: colors.grey[600],
              dark: colors.grey[300],
            },
            success: {
              main: colors.success[100],
            },
            warning: {
              main: colors.warning[100],
            },
            error: {
              main: colors.error[100],
            },
            background: {
              default: colors.grey[600],
              form: colors.grey[600],
              banner: colors.primary[200],
              autocomplete: colors.grey[350],
              dataGrid: colors.grey[600],
              layout: colors.grey[100],
            },
            header: {
              header: "#F1F7FF",
            },
            action: {
              activatedOpacity: 0.2,
            },
          }),
    },
    typography: {
      fontSize: 11,
      fontFamily: ["Poppins", "sans-serif"].join(","),
      h1: {
        fontSize: 40,
      },
      h2: {
        fontSize: 32,
      },
      h3: {
        fontSize: 24,
      },
      h4: {
        fontSize: 20,
      },
      h5: {
        fontSize: 16,
      },
      h6: {
        fontSize: 14,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          contained: {
            backgroundColor: alpha(colors.primary[300], 0.8),
            "&:hover": {
              backgroundColor: alpha(colors.primary[300], 1),
            },
          },
        },
      },

      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiInputBase-root": {
              size: "small",
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          root: {
            backdropFilter: "blur(5px)",
          },
          paper: {
            width: "90vw",
            borderRadius: 4,
            maxHeight: "90vh",
            "& .MuiDialogTitle-root": {
              fontWeight: "bold",
            },
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            fontSize: 12,
            pb: 0,
            height: "60vh",
            width: "100%",
            border: "none",
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none",
              backgroundColor: (theme: Theme) =>
                alpha(colors.grey[100], theme.palette.action.activatedOpacity),
            },
            "& .MuiDataGrid-columnHeader:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeader-within": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
            },
            ".MuiDataGrid-row:focus": {
              borderRadius: 0,
            },
            ".MuiDataGrid-row:focus-within": {
              borderRadius: 0,
            },
            "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer":
              {
                display: "none",
              },
            ".MuiDataGrid-columnHeaderTitle": {
              fontSize: 13,
              fontWeight: "bold",
              color: colors.grey[300],
            },
          }
        },
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
  };
};
